
import { Field, ErrorMessage } from "formik";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import { currencyOptions } from "../../utils/currencyOptions";
import { formatNumberWithCommas } from "../../utils/formatNumber";

const repaymentFrequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

export const creditCardInitialValues = {
  issuer: "",
  creditLimit: "",
  currentBalance: "",
  interestRate: "",
  minimumPayment: "",
  repaymentFrequency: "",
  statementDate: null,
  paymentDueDate: null,
  currency: "",
  startDate: null,
   name: "",
};

export const creditCardValidationSchema = Yup.object().shape({
  name: Yup.string()
      .min(3, "Min length: 3 characters")
      .max(100, "Max length: 100 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      )
      .required("Name is required"),
  issuer: Yup.string().required("Card name/issuer is required"),
  creditLimit: Yup.number()
    .typeError("Must be a positive number")
    .required("Credit limit is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  currentBalance: Yup.number()
    .typeError("Must be a positive number")
    .required("Current balance is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  interestRate: Yup.number()
    .typeError("Must be a positive number")
    .required("Interest rate is required")
    .positive("Must be a positive number > 0")
    .min(0.01, "Must be greater than 0"),
  minimumPayment: Yup.number()
    .typeError("Must be a positive number")
    .required("Minimum payment is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  repaymentFrequency: Yup.string().required("Repayment frequency is required"),
  statementDate: Yup.date().required("Statement date is required"),
  paymentDueDate: Yup.date().required("Payment due date is required"),
  currency: Yup.string().required("Currency is required"),
  startDate: Yup.date().required("Start date is required"),
});

const CreditCardForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  return (
    <>
     <div className="col-md-12 mb-4">
        <label className="form-label">
          Liability Name<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="name"
          placeholder="e.g., Credit Card, Visa, MasterCard"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="name" component="div" className="invalid-feedback" />
      </div>
    <div className="row">
      {/* Card Issuer */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Card Name/Issuer<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="issuer"
          placeholder="e.g., Chase, Citi"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.issuer && touched.issuer ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="issuer" component="div" className="invalid-feedback" />
      </div>

      {/* Credit Limit */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Credit Limit<span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
          />
          <Field
            type="text"
            name="creditLimit"
            className={`form-control ${errors.creditLimit && touched.creditLimit ? "is-invalid" : ""}`}
            placeholder="e.g., 50,000"
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
            value={values.creditLimit ? formatNumberWithCommas(values.creditLimit) : ""}
            onChange={(e) => {
              let unformatted = e.target.value.replace(/[^\d.]/g, "")
              const parts = unformatted.split(".")
              if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
              if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
              setFieldValue("creditLimit", unformatted || "")
            }}
            onBlur={(e) => {
              const unformatted = e.target.value.replace(/,/g, "")
              if (unformatted) {
                setFieldValue("creditLimit", unformatted)
              }
            }}
          />
        </div>
        <ErrorMessage name="creditLimit" component="div" className="invalid-feedback" />
        <ErrorMessage name="currency" component="div" className="invalid-feedback" />
      </div>

      {/* Start Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Start Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.startDate ? new Date(values.startDate) : null}
          onChange={date => setFieldValue("startDate", date)}
          dateFormat="yyyy-MM-dd"
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.startDate && touched.startDate ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="startDate" component="div" className="invalid-feedback" />
      </div>

      {/* Current Balance */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Current Balance<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="currentBalance"
          className={`form-control ${errors.currentBalance && touched.currentBalance ? "is-invalid" : ""}`}
          placeholder="e.g., 50,000"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.currentBalance ? formatNumberWithCommas(values.currentBalance) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("currentBalance", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("currentBalance", unformatted)
            }
          }}
        />
        <ErrorMessage name="currentBalance" component="div" className="invalid-feedback" />
      </div>

      {/* Interest Rate */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Interest Rate (APR%)<span className="text-danger">*</span>
        </label>
        <Field
          type="number"
          name="interestRate"
          className={`form-control ${errors.interestRate && touched.interestRate ? "is-invalid" : ""}`}
          step="0.01"
          placeholder="e.g., 15.99"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="interestRate" component="div" className="invalid-feedback" />
      </div>

      {/* Minimum Payment */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Minimum Payment<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="minimumPayment"
          className={`form-control ${errors.minimumPayment && touched.minimumPayment ? "is-invalid" : ""}`}
          placeholder="e.g., 2,500"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.minimumPayment ? formatNumberWithCommas(values.minimumPayment) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("minimumPayment", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("minimumPayment", unformatted)
            }
          }}
        />
        <ErrorMessage name="minimumPayment" component="div" className="invalid-feedback" />
      </div>

      {/* Repayment Frequency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Repayment Frequency<span className="text-danger">*</span>
        </label>
        <Select
          options={repaymentFrequencyOptions}
          value={repaymentFrequencyOptions.find(option => option.value === values.repaymentFrequency)}
          onChange={(option) => setFieldValue("repaymentFrequency", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
        />
        <ErrorMessage name="repaymentFrequency" component="div" className="text-danger small" />
      </div>

      {/* Statement Date (react-datepicker) */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Statement Date<span className="text-danger">*</span>
        </label>
        <DatePicker
          selected={values.statementDate ? new Date(values.statementDate) : null}
          onChange={(date) => setFieldValue("statementDate", date)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.statementDate && touched.statementDate ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="statementDate" component="div" className="text-danger small" />
      </div>

      {/* Payment Due Date (react-datepicker) */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Payment Due Date<span className="text-danger">*</span>
        </label>
        <DatePicker
          selected={values.paymentDueDate ? new Date(values.paymentDueDate) : null}
          onChange={(date) => setFieldValue("paymentDueDate", date)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.paymentDueDate && touched.paymentDueDate ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="paymentDueDate" component="div" className="text-danger small" />
      </div>
    </div>
    </>
  );
};

export default CreditCardForm;
